/**
 * サブスクリプション解約取り消し・復活確認ダイアログ
 * @module components/subscription/UndoCancellationConfirmDialog
 */

import { Typography, Space, Alert } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { withDevModalFilePath } from '../common';

const { Text, Title } = Typography;
const { Modal } = withDevModalFilePath('client/src/components/subscription/UndoCancellationConfirmDialog.tsx');

interface UndoCancellationConfirmDialogProps {
  open: boolean;
  cancelEffectiveDate: Date | string | null | undefined;
  /** 解約済み（canceled）状態からの復活かどうか */
  isRestoreFromCanceled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

/**
 * 日付フォーマット
 */
const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return '-';
  return format(new Date(date), 'yyyy年MM月dd日', { locale: ja });
};

/**
 * サブスクリプション解約取り消し・復活確認ダイアログ
 */
export function UndoCancellationConfirmDialog({
  open,
  cancelEffectiveDate,
  isRestoreFromCanceled = false,
  onConfirm,
  onCancel,
  loading = false,
}: UndoCancellationConfirmDialogProps) {
  // 復活モード（canceled → active）
  if (isRestoreFromCanceled) {
    return (
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 20 }} />
            <Title level={4} style={{ margin: 0 }}>
              サブスクリプションを復活しますか？
            </Title>
          </Space>
        }
        open={open}
        onOk={onConfirm}
        onCancel={onCancel}
        okText="サブスクリプションを復活する"
        okType="primary"
        cancelText="キャンセル"
        okButtonProps={{ loading }}
        width={520}
        destroyOnHidden
      >
        <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 16 }}>
          <Alert
            type="warning"
            showIcon
            message="過去のサブスクリプションを復活します"
            description="この操作は、解約済みのサブスクリプションをアクティブ状態に戻します。"
          />

          <div>
            <Title level={5} style={{ marginBottom: 8 }}>復活後の変更点</Title>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>サブスクリプションステータスが「アクティブ」になります</li>
              <li>すべてのユーザーが再度ログインできるようになります</li>
              <li>データ削除予定がある場合はキャンセルされます</li>
            </ul>
          </div>

          <Alert
            type="info"
            showIcon
            message="注意事項"
            description={
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Stripe決済の場合、請求サイクルは自動的に再開されない場合があります</li>
                <li>銀行振込の場合、別途入金確認が必要な場合があります</li>
                <li>この操作は監査ログに記録されます</li>
              </ul>
            }
          />
        </Space>
      </Modal>
    );
  }

  // 通常モード（suspended → active: 解約予定の取り消し）
  return (
    <Modal
      title={
        <Space>
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
          <Title level={4} style={{ margin: 0 }}>
            解約を取り消しますか？
          </Title>
        </Space>
      }
      open={open}
      onOk={onConfirm}
      onCancel={onCancel}
      okText="解約を取り消す"
      okType="primary"
      cancelText="キャンセル"
      okButtonProps={{ loading }}
      width={500}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%', marginTop: 16 }}>
        <Text>
          現在、<Text strong>{formatDate(cancelEffectiveDate)}</Text> に解約が予定されています。
        </Text>

        <Text>
          解約を取り消すと、契約は継続され、通常通りサービスをご利用いただけます。
        </Text>

        <div style={{ marginTop: 8, padding: '12px 16px', backgroundColor: '#f0f9ff', borderRadius: 4, border: '1px solid #91caff' }}>
          <Text type="secondary">
            この操作により、解約予定がキャンセルされ、サブスクリプションが継続されます。
          </Text>
        </div>
      </Space>
    </Modal>
  );
}
